import { collections, firestoreHelpers } from '@/lib/firebase';
import type { Ride, RideStatus, Stop, GeoPoint } from '@/types';

export const rideService = {
  // Create new ride request
  async createRide(rideData: Partial<Ride>): Promise<string> {
    const newRide: Partial<Ride> = {
      ...rideData,
      status: 'searching',
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    return await firestoreHelpers.createDocument(collections.rides, newRide);
  },

  // Get ride by ID
  async getRide(rideId: string): Promise<Ride | null> {
    return await firestoreHelpers.getDocument<Ride>(collections.rides, rideId);
  },

  // Update ride
  async updateRide(rideId: string, data: Partial<Ride>): Promise<void> {
    await firestoreHelpers.updateDocument(collections.rides, rideId, data);
  },

  // Subscribe to ride changes
  subscribeToRide(rideId: string, callback: (ride: Ride | null) => void) {
    return firestoreHelpers.subscribeToDocument<Ride>(
      collections.rides,
      rideId,
      callback
    );
  },

  // Assign driver to ride
  async assignDriver(rideId: string, driverId: string): Promise<void> {
    await this.updateRide(rideId, {
      driverId,
      status: 'driver_assigned',
      updatedAt: new Date(),
    });
  },

  // Driver arrived at pickup
  async driverArrived(rideId: string): Promise<void> {
    await this.updateRide(rideId, {
      status: 'driver_arrived',
      updatedAt: new Date(),
    });
  },

  // Start ride
  async startRide(rideId: string): Promise<void> {
    await this.updateRide(rideId, {
      status: 'in_progress',
      startedAt: new Date(),
      updatedAt: new Date(),
    });
  },

  // Complete ride
  async completeRide(rideId: string, finalPrice: number): Promise<void> {
    await this.updateRide(rideId, {
      status: 'completed',
      finalPrice,
      completedAt: new Date(),
      updatedAt: new Date(),
    });
  },

  // Cancel ride
  async cancelRide(rideId: string, reason: string, cancelledBy: 'passenger' | 'driver'): Promise<void> {
    await this.updateRide(rideId, {
      status: 'cancelled',
      cancelReason: reason,
      cancelledBy,
      cancelledAt: new Date(),
      updatedAt: new Date(),
    });
  },

  // Add stop
  async addStop(rideId: string, stop: Stop): Promise<void> {
    const ride = await this.getRide(rideId);
    if (ride) {
      const stops = ride.stops || [];
      stops.push({ ...stop, order: stops.length + 1 });
      await this.updateRide(rideId, { stops });
    }
  },

  // Remove stop
  async removeStop(rideId: string, stopId: string): Promise<void> {
    const ride = await this.getRide(rideId);
    if (ride && ride.stops) {
      const stops = ride.stops.filter(s => s.id !== stopId);
      // Reorder stops
      stops.forEach((stop, index) => {
        stop.order = index + 1;
      });
      await this.updateRide(rideId, { stops });
    }
  },

  // Complete stop
  async completeStop(rideId: string, stopId: string): Promise<void> {
    const ride = await this.getRide(rideId);
    if (ride && ride.stops) {
      const stops = ride.stops.map(s => 
        s.id === stopId ? { ...s, completed: true } : s
      );
      await this.updateRide(rideId, { stops });
    }
  },

  // Rate ride
  async rateRide(
    rideId: string, 
    rating: number, 
    review: string, 
    by: 'passenger' | 'driver'
  ): Promise<void> {
    if (by === 'passenger') {
      await this.updateRide(rideId, { 
        driverRating: rating,
        driverReview: review 
      });
    } else {
      await this.updateRide(rideId, { 
        passengerRating: rating,
        passengerReview: review 
      });
    }
  },

  // Update route
  async updateRoute(rideId: string, route: GeoPoint[]): Promise<void> {
    await this.updateRide(rideId, { route });
  },

  // Get passenger rides
  async getPassengerRides(passengerId: string, limitCount?: number): Promise<Ride[]> {
    return await firestoreHelpers.queryDocuments<Ride>(
      collections.rides,
      [{ field: 'passengerId', operator: '==', value: passengerId }],
      'createdAt',
      'desc',
      limitCount
    );
  },

  // Get driver rides
  async getDriverRides(driverId: string, limitCount?: number): Promise<Ride[]> {
    return await firestoreHelpers.queryDocuments<Ride>(
      collections.rides,
      [{ field: 'driverId', operator: '==', value: driverId }],
      'createdAt',
      'desc',
      limitCount
    );
  },

  // Get active ride for passenger
  async getPassengerActiveRide(passengerId: string): Promise<Ride | null> {
    const rides = await firestoreHelpers.queryDocuments<Ride>(
      collections.rides,
      [
        { field: 'passengerId', operator: '==', value: passengerId },
        { field: 'status', operator: 'in', value: ['searching', 'driver_assigned', 'driver_arrived', 'in_progress'] }
      ],
      'createdAt',
      'desc',
      1
    );
    return rides.length > 0 ? rides[0] : null;
  },

  // Get active ride for driver
  async getDriverActiveRide(driverId: string): Promise<Ride | null> {
    const rides = await firestoreHelpers.queryDocuments<Ride>(
      collections.rides,
      [
        { field: 'driverId', operator: '==', value: driverId },
        { field: 'status', operator: 'in', value: ['driver_assigned', 'driver_arrived', 'in_progress'] }
      ],
      'createdAt',
      'desc',
      1
    );
    return rides.length > 0 ? rides[0] : null;
  },

  // Get available rides for driver (searching rides)
  async getAvailableRides(): Promise<Ride[]> {
    return await firestoreHelpers.queryDocuments<Ride>(
      collections.rides,
      [{ field: 'status', operator: '==', value: 'searching' }],
      'createdAt',
      'desc'
    );
  },

  // Get all active rides (for admin)
  async getAllActiveRides(): Promise<Ride[]> {
    return await firestoreHelpers.queryDocuments<Ride>(
      collections.rides,
      [{ field: 'status', operator: 'in', value: ['searching', 'driver_assigned', 'driver_arrived', 'in_progress'] }],
      'createdAt',
      'desc'
    );
  },

  // Get rides by status (for admin)
  async getRidesByStatus(status: RideStatus, limitCount?: number): Promise<Ride[]> {
    return await firestoreHelpers.queryDocuments<Ride>(
      collections.rides,
      [{ field: 'status', operator: '==', value: status }],
      'createdAt',
      'desc',
      limitCount
    );
  },

  // Get today's rides (for admin)
  async getTodayRides(): Promise<Ride[]> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return await firestoreHelpers.queryDocuments<Ride>(
      collections.rides,
      [{ field: 'createdAt', operator: '>=', value: today }],
      'createdAt',
      'desc'
    );
  },

  // Get all rides (for admin)
  async getAllRides(limitCount?: number): Promise<Ride[]> {
    return await firestoreHelpers.queryDocuments<Ride>(
      collections.rides,
      [],
      'createdAt',
      'desc',
      limitCount
    );
  },

  // Search rides (for admin)
  async searchRides(searchTerm: string): Promise<Ride[]> {
    const rides = await this.getAllRides(100);
    return rides.filter(r => 
      r.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.passengerId.includes(searchTerm) ||
      (r.driverId || '').includes(searchTerm)
    );
  },

  // Subscribe to passenger rides
  subscribeToPassengerRides(passengerId: string, callback: (rides: Ride[]) => void) {
    return firestoreHelpers.subscribeToQuery<Ride>(
      collections.rides,
      [{ field: 'passengerId', operator: '==', value: passengerId }],
      callback
    );
  },

  // Subscribe to driver rides
  subscribeToDriverRides(driverId: string, callback: (rides: Ride[]) => void) {
    return firestoreHelpers.subscribeToQuery<Ride>(
      collections.rides,
      [{ field: 'driverId', operator: '==', value: driverId }],
      callback
    );
  },

  // Subscribe to available rides
  subscribeToAvailableRides(callback: (rides: Ride[]) => void) {
    return firestoreHelpers.subscribeToQuery<Ride>(
      collections.rides,
      [{ field: 'status', operator: '==', value: 'searching' }],
      callback
    );
  },

  // Calculate estimated price
  calculatePrice(distanceKm: number, durationMinutes: number, rideType: string = 'immediate'): number {
    const basePrice = 500; // Base price in SDG
    const pricePerKm = 150;
    const pricePerMinute = 10;
    
    let price = basePrice + (distanceKm * pricePerKm) + (durationMinutes * pricePerMinute);
    
    // Apply ride type multiplier
    switch (rideType) {
      case 'comfort':
        price *= 1.3;
        break;
      case 'premium':
        price *= 1.8;
        break;
      case 'family':
        price *= 1.5;
        break;
      default:
        break;
    }
    
    return Math.round(price);
  },

  // Apply coupon discount
  applyCoupon(price: number, couponValue: number, couponType: 'percentage' | 'fixed'): number {
    if (couponType === 'percentage') {
      return Math.round(price * (1 - couponValue / 100));
    } else {
      return Math.max(0, price - couponValue);
    }
  }
};
